// src/pages/BlogDetail.jsx
// Updated by Claude on 20.05.2025 to add SEO optimization
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../components/common/NavBar';
import MiniHero from '../components/common/MiniHero';
import Footer from '../components/common/Footer';
import SEO from '../components/common/SEO';
import { getBlogPostSchema, getBreadcrumbsSchema } from '../utils/schema';
import { generateMetaDescription, generateKeywords, getImageAltText } from '../utils/seo';
import { get } from '../utils/api';
import { getUserLanguage } from '../utils/language';
import { formatDate } from '../utils/formatters';
import { IMAGE_BASE_URL } from '../config';

const BlogDetail = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const language = getUserLanguage();

  useEffect(() => {
    const fetchPostDetail = async () => {
      try {
        setLoading(true);
        const headers = { 'accept-language': language };
        const data = await get(`/blog/${slug}`, headers);

        if (!data || !data.id) {
          throw new Error('Post not found');
        }

        setPost(data);

        // Fetch related posts from the same category
        if (data.category) {
          const relatedData = await get(`/blog?category=${data.category}&limit=3`, headers);
          // Filter out the current post
          const filtered = (relatedData.posts || []).filter(p => p.id !== data.id);
          setRelatedPosts(filtered.slice(0, 3));
        }

        setError(null);
      } catch (error) {
        console.error('[BlogDetail] Error fetching post:', error);
        setError(language === 'cs' ? 'Příspěvek nebyl nalezen' : 'Post not found');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchPostDetail();
      // Scroll to top when post changes
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [slug, language]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <Navbar language={language} />
        <MiniHero title={language === 'cs' ? 'Blog' : 'Blog'} />
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-600"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-black">
        <Navbar language={language} />
        <MiniHero title={language === 'cs' ? 'Blog' : 'Blog'} />
        <div className="container mx-auto px-4 py-20 text-center">
          <div className="text-white text-xl mb-6">
            {error || (language === 'cs' ? 'Příspěvek nebyl nalezen' : 'Post not found')}
          </div>
          <Link to="/blog" className="btn-drift">
            <i className="fas fa-arrow-left mr-2"></i>
            {language === 'cs' ? 'Zpět na blog' : 'Back to blog'}
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  // Format tags for display
  const tags = post.tags ? post.tags.split(',').map(tag => tag.trim()) : [];

  // Generate blog-specific schema
  const blogPostSchema = getBlogPostSchema({
    ...post,
    image_url: post.image_path ? `${IMAGE_BASE_URL}${post.image_path}` : `${IMAGE_BASE_URL}/images/blog/placeholder.jpg`
  }, language);

  // Generate breadcrumbs schema
  const breadcrumbsSchema = getBreadcrumbsSchema([
    { name: language === 'cs' ? 'Domů' : 'Home', path: '/' },
    { name: 'Blog', path: '/blog' },
    { name: post.title, path: `/blog/${post.slug}` }
  ], language);

  // Generate meta description if not provided
  const metaDescription = post.summary || generateMetaDescription(post.content, 160);
  
  // Generate keywords from content and category
  const metaKeywords = generateKeywords(post.content, post.category, 
    `drift blog, drift team, ${post.category || ''}, ${tags.join(', ')}`);

  // Generate alt text for images
  const imageAltText = getImageAltText(post.image_path) || post.title;

  return (
    <div className="min-h-screen bg-black">
      <SEO
        title={post.title}
        description={metaDescription}
        image={post.image_path ? `${IMAGE_BASE_URL}${post.image_path}` : `${IMAGE_BASE_URL}/images/blog/placeholder.jpg`}
        article={true}
        keywords={metaKeywords}
        canonical={`${language === 'cs' ? 'https://norisknodrift.cz' : IMAGE_BASE_URL}/blog/${post.slug}`}
        schema={{
          '@context': 'https://schema.org',
          '@graph': [blogPostSchema, breadcrumbsSchema]
        }}
      />
      
      <Navbar language={language} />
      <MiniHero
        title={post.title || (language === 'cs' ? 'Blog' : 'Blog')}
        description={post.summary}
      />

      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Main content */}
            <div className="lg:w-3/4">
              <article className="bg-dark-gray rounded-lg overflow-hidden shadow-xl">
                {/* Featured image */}
                {post.image_path && (
                  <div className="h-96 w-full relative">
                    <img
                      src={`${IMAGE_BASE_URL}${post.image_path}`}
                      alt={imageAltText}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/images/blog/placeholder.jpg';
                      }}
                      loading="eager"
                      width="800"
                      height="450"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent h-1/3"></div>
                  </div>
                )}

                {/* Post metadata */}
                <div className="px-6 pt-6 pb-2 flex flex-wrap items-center justify-between">
                  <div className="flex items-center mb-4">
                    <span className="text-gray-400 text-sm">
                      {formatDate(post.published_date)}
                    </span>
                    <span className="mx-2 text-gray-500">•</span>
                    <span className="text-gray-400 text-sm">
                      {language === 'cs' ? 'Autor:' : 'By'} {post.author_name || 'Admin'}
                    </span>
                  </div>

                  {post.category && (
                    <Link to={`/blog?category=${post.category}`} className="mb-4">
                      <span className="bg-red-600/20 text-red-500 px-3 py-1 rounded text-sm">
                        {post.category}
                      </span>
                    </Link>
                  )}
                </div>

                {/* Post content */}
                <div className="px-6 py-6">
                  <h1 className="text-3xl font-title font-bold text-white mb-4">
                    {post.title}
                  </h1>
                  <div className="gradient-line mb-6"></div>

                  {/* Post body content */}
                  <div
                    className="text-gray-300 prose prose-invert max-w-none"
                    dangerouslySetInnerHTML={{ __html: post.content }}
                  ></div>

                  {/* Post tags */}
                  {tags.length > 0 && (
                    <div className="mt-8 pt-6 border-t border-gray-700">
                      <h3 className="text-white text-lg font-semibold mb-3">
                        {language === 'cs' ? 'Tagy:' : 'Tags:'}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {tags.map((tag, index) => (
                          <Link
                            key={index}
                            to={`/blog?tag=${tag}`}
                            className="bg-gray-800 text-gray-400 hover:bg-gray-700 px-3 py-1 rounded-full text-sm"
                          >
                            {tag}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </article>

              {/* Related posts */}
              {relatedPosts.length > 0 && (
                <div className="mt-12">
                  <h2 className="text-2xl font-title font-bold text-white mb-6">
                    {language === 'cs' ? 'Související příspěvky' : 'Related Posts'}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {relatedPosts.map((relatedPost) => (
                      <Link
                        to={`/blog/${relatedPost.slug}`}
                        key={relatedPost.id}
                        className="bg-dark-gray rounded-lg overflow-hidden shadow-lg Hover:shadow-xl transition-shadow"
                      >
                        <div className="h-48 relative">
                          <img
                            src={
                              relatedPost.image_path
                                ? `${IMAGE_BASE_URL}${relatedPost.image_path}`
                                : '/images/blog/placeholder.jpg'
                            }
                            alt={getImageAltText(relatedPost.image_path) || relatedPost.title}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = '/images/blog/placeholder.jpg';
                            }}
                            loading="lazy"
                            width="300"
                            height="200"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"></div>
                        </div>
                        <div className="p-4">
                          <h3 className="text-white font-semibold mb-2 hover:text-red-500 transition-colors">
                            {relatedPost.title}
                          </h3>
                          <p className="text-gray-400 text-sm line-clamp-2">
                            {relatedPost.summary}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Back button */}
              <div className="mt-8">
                <Link
                  to="/blog"
                  className="text-white hover:text-red-500 transition-colors flex items-center"
                >
                  <i className="fas fa-arrow-left mr-2"></i>
                  {language === 'cs' ? 'Zpět na všechny příspěvky' : 'Back to all posts'}
                </Link>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:w-1/4">
              <div className="bg-dark-gray p-6 rounded shadow-lg sticky top-24">
                <h3 className="text-xl font-title font-bold text-white mb-4">
                  {language === 'cs' ? 'Informace o příspěvku' : 'Post Information'}
                </h3>
                <div className="gradient-line mb-4"></div>

                <div className="space-y-4">
                  <div>
                    <h4 className="text-gray-400 text-sm">
                      {language === 'cs' ? 'Publikováno:' : 'Published:'}
                    </h4>
                    <p className="text-white">{formatDate(post.published_date)}</p>
                  </div>

                  {post.updated_date && post.updated_date !== post.published_date && (
                    <div>
                      <h4 className="text-gray-400 text-sm">
                        {language === 'cs' ? 'Aktualizováno:' : 'Updated:'}
                      </h4>
                      <p className="text-white">{formatDate(post.updated_date)}</p>
                    </div>
                  )}

                  <div>
                    <h4 className="text-gray-400 text-sm">
                      {language === 'cs' ? 'Autor:' : 'Author:'}
                    </h4>
                    <p className="text-white">{post.author_name || 'Admin'}</p>
                  </div>

                  {post.category && (
                    <div>
                      <h4 className="text-gray-400 text-sm">
                        {language === 'cs' ? 'Kategorie:' : 'Category:'}
                      </h4>
                      <Link
                        to={`/blog?category=${post.category}`}
                        className="text-red-500 hover:text-red-400"
                      >
                        {post.category}
                      </Link>
                    </div>
                  )}

                  {/* Social share buttons */}
                  <div className="pt-4 border-t border-gray-700">
                    <h4 className="text-gray-400 text-sm mb-2">
                      {language === 'cs' ? 'Sdílet:' : 'Share:'}
                    </h4>
                    <div className="flex space-x-3">
                      <a
                        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                          window.location.href
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors"
                      >
                        <i className="fab fa-facebook-f"></i>
                      </a>

                      <a
                        href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(
                          window.location.href
                        )}&text=${encodeURIComponent(post.title)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-8 h-8 bg-blue-400 text-white rounded-full flex items-center justify-center hover:bg-blue-500 transition-colors"
                      >
                        <i className="fab fa-twitter"></i>
                      </a>

                      <a
                        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                          window.location.href
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-8 h-8 bg-blue-800 text-white rounded-full flex items-center justify-center hover:bg-blue-900 transition-colors"
                      >
                        <i className="fab fa-linkedin-in"></i>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogDetail;